import React from 'react';
import { Sparkles, Flame, Lock, Bell, Ticket, ChevronRight, ShieldCheck, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { PRE_REGISTRO_2027_URL } from '../config/constants';
import { WHATSAPP_2027_URL } from './PostEventSection';
import { PreregistroCounter } from './PreregistroCounter';
import { trackPreregistroClick } from '../hooks/usePreregistroCount';

const BG = 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/kcrd7vym_WhatsApp%20Image%202026-08-29%20at%205.57.30%20AM.jpeg&w=1600&q=70&output=webp';

const PreRegistro2027Section = ({ compact = false }) => {
  const { language } = useLanguage();
  const isES = language === 'es';

  const perks = [
    { icon: Bell, t: isES ? 'Sede y fechas antes que nadie' : 'Venue & dates before anyone', d: isES ? 'Te lo contamos primero a ti, no a las redes.' : 'You hear it first, not social media.' },
    { icon: Ticket, t: isES ? 'Acceso a la preventa' : 'Presale access', d: isES ? 'La edición 20 se agotó semanas antes. Esta vez, tú vas primero.' : 'Edition 20 sold out weeks early. This time, you go first.' },
    { icon: Lock, t: isES ? 'Lugar prioritario' : 'Priority spot', d: isES ? 'Lista prioritaria por orden de pre-registro.' : 'Priority list by pre-registration order.' },
  ];

  return (
    <section id="preregistro" className="relative overflow-hidden bg-[#071528] scroll-mt-20" data-testid="preregistro-2027-section">
      <img src={BG} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#071528] via-[#071528]/80 to-[#071528]" aria-hidden="true"></div>
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] bg-[#FF6B35]/20 rounded-full blur-[160px]" aria-hidden="true"></div>
      <div aria-hidden="true" className="absolute -right-8 -bottom-28 select-none pointer-events-none leading-none font-black text-white/[0.04] text-[22rem] md:text-[34rem]">21</div>

      <div className={`relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${compact ? 'py-16' : 'py-24 lg:py-28'}`}>
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[#FF6B35] text-white rounded-full px-4 py-1.5 mb-6 shadow-[0_0_30px_rgba(255,107,53,0.5)]" data-testid="preregistro-badge">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
            </span>
            <span className="text-xs font-black uppercase tracking-[0.2em]">{isES ? 'Pre-registro abierto · 21ª Convención Nacional 2027' : 'Pre-registration open · 21st National Convention 2027'}</span>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05]">
            {isES ? 'Sé de los primeros.' : 'Be among the first.'}
            <span className="block bg-gradient-to-r from-[#FF6B35] via-[#FFB073] to-[#D4AF37] bg-clip-text text-transparent mt-2">
              {isES ? 'La edición 21 ya está en camino.' : 'Edition 21 is on its way.'}
            </span>
          </h2>

          <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed">
            {isES
              ? 'La 20ª Convención fue Sold Out semanas antes y cientos se quedaron fuera. Pre-regístrate hoy y recibe antes que nadie sede, fechas, preventa y todos los detalles de la próxima edición.'
              : 'The 20th Convention sold out weeks in advance and hundreds missed out. Pre-register today and be the first to receive venue, dates, presale and every detail of the next edition.'}
          </p>

          <div className="mt-8 flex justify-center">
            <PreregistroCounter />
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={PRE_REGISTRO_2027_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackPreregistroClick}
              data-testid="preregistro-2027-cta"
              className="reservar-cta group relative inline-flex items-center gap-3 bg-[#FF6B35] hover:bg-[#E55A28] text-white font-black px-9 py-5 rounded-full text-lg shadow-[0_20px_60px_rgba(255,107,53,0.45)] transition-all hover:-translate-y-1 hover:scale-[1.03]"
            >
              <Flame className="w-6 h-6" />
              {isES ? 'Pre-regístrate para 2027' : 'Pre-register for 2027'}
              <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={WHATSAPP_2027_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="preregistro-whatsapp-link"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white font-semibold px-4 py-3 transition-colors"
            >
              <MessageCircle className="w-5 h-5 text-[#25D366]" />
              {isES ? 'O escríbenos por WhatsApp' : 'Or message us on WhatsApp'}
            </a>
          </div>
          <p className="mt-4 text-xs text-white/45 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" />
            {isES ? 'Sin compromiso · Toma menos de 1 minuto · Sin costo' : 'No commitment · Under 1 minute · Free'}
          </p>
        </div>

        {!compact && (
          <div className="mt-14 grid sm:grid-cols-3 gap-4" data-testid="preregistro-perks">
            {perks.map((p) => (
              <div key={p.t} className="group relative bg-white/[0.05] hover:bg-white/[0.09] backdrop-blur-md border border-white/10 hover:border-[#D4AF37]/50 rounded-2xl p-6 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#D4AF37] flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform">
                  <p.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-white font-bold text-lg leading-tight">{p.t}</h3>
                <p className="text-white/55 text-sm mt-2 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        )}

        {!compact && (
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-white/60" data-testid="preregistro-social-proof">
            <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#D4AF37]" />{isES ? '+800 asistentes en 2026' : '+800 attendees in 2026'}</span>
            <span className="inline-flex items-center gap-2"><Flame className="w-4 h-4 text-[#FF6B35]" />{isES ? 'Sold Out semanas antes' : 'Sold out weeks in advance'}</span>
            <span className="inline-flex items-center gap-2"><Ticket className="w-4 h-4 text-[#D4AF37]" />{isES ? '+60 marcas patrocinadoras' : '+60 sponsor brands'}</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default PreRegistro2027Section;
